import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { PolicyDetails } from 'generated-api/bff/api';
import { getApiInstance } from 'services/Api';

// Async thunk for fetching a single policy
export const fetchPolicy = createAsyncThunk<
	PolicyDetails,
	{ policyNo: string; sourceSystem: string },
	{ rejectValue: string }
>('policy/fetchPolicy', async ({ policyNo, sourceSystem }, { rejectWithValue }) => {
	try {
		const apiInstance = await getApiInstance('/commercial-selfservice/');
		const response = await apiInstance.bffApiServiceLayer.getPolicy(policyNo, sourceSystem);

		// Ensure the response has the expected structure
		if (!response.data) {
			return rejectWithValue('Policy not found');
		}

		return response.data; // Returns PolicyDetails
	} catch (error: any) {
		return rejectWithValue(error.message || 'Failed to fetch policy');
	}
});

interface PolicyState {
	policy: PolicyDetails | null; // Store the fetched policy details
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: PolicyState = {
	policy: null,
	status: HttpStatusCode.Idle,
	error: null,
};

// Slice for fetching a single policy
const policySlice = createSlice({
	name: 'policy',
	initialState,
	reducers: {
		clearPolicyCache(state) {
			state.policy = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPolicy.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchPolicy.fulfilled, (state, action) => {
				state.status = HttpStatusCode.OK;
				state.policy = action.payload; // Set fetched policy in the state
			})
			.addCase(fetchPolicy.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.payload || 'Failed to fetch policy';
			});
	},
});

export const { clearPolicyCache } = policySlice.actions;
export default policySlice.reducer;
